import {
    Alert,
    Badge,
    Button,
    Center,
    Container,
    Divider,
    Group,
    Loader,
    Paper,
    Stack,
    Switch,
    Text,
    Title
} from "@mantine/core";
import {
    Email,
    Error,
    Group as GroupIcon,
    Handshake,
    Home,
    Language,
    Queue,
    TrendingUp
} from "@mui/icons-material";
import {
    showErrorNotification,
    useGetEmailPreferencesQuery,
    useUpdateEmailPreferenceMutation
} from "@therapprove/common";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { unsubscribeEmailBase64ToPayload } from "../utils/payload.utils";

const PRIMARY_COLOR = "var(--mantine-color-primary-9)";

// Email preference type definitions with descriptions
const EMAIL_PREFERENCE_TYPES = {
    REFERRAL_FOR_CAREGIVERS: {
        label: "Direct Referrals",
        description:
            "Receive notifications when you are referred to a new organization.",
        icon: Handshake,
        category: "Engagement"
    },
    GLOBAL_RETENTION: {
        label: "Global Referrals Participation",
        description:
            "Choose whether to remain in global referrals to connect with new organizations while maintaining your current connections.",
        icon: TrendingUp,
        category: "Engagement"
    },
    GLOBAL_INVITE: {
        label: "Global Invitations",
        description:
            "Receive invitations to join global referrals and expand your network while maintaining your current organization connections.",
        icon: GroupIcon,
        category: "Invitations"
    },
    QUEUE_ORGANIZATION_INVITE: {
        label: "Pipeline Organization Invitations",
        description:
            "Receive invitations from organizations to join their pipeline.",
        icon: Queue,
        category: "Invitations"
    },
    WEBSITE_LEAD_ACCEPTED: {
        label: "Form Response Accepted",
        description:
            "Receive notifications when your website form response (on an organization's website) is accepted by an organization.",
        icon: Language,
        category: "Website Forms"
    }
} as const;

export const ManageEmailPreferences = () => {
    // Get the encoded unsubscribe key parameter from the URL path
    const { encodedUnsubscribeKeys } = useParams<{
        encodedUnsubscribeKeys: string;
    }>();

    // State variables
    const [pageState, setPageState] = useState<
        "error" | "loading" | "ready" | null
    >("loading");
    const [preferences, setPreferences] = useState<
        Record<string, { emailPreferenceId: string; shouldSend: boolean }>
    >({});

    // Decode the unsubscribe key
    const decodedKeys = useMemo(() => {
        if (!encodedUnsubscribeKeys) return undefined;

        try {
            const payload = unsubscribeEmailBase64ToPayload(
                encodedUnsubscribeKeys
            );
            if (!payload) {
                setPageState("error");
                return undefined;
            }

            return payload;
        } catch (e) {
            setPageState("error");
            console.error(e);
            return undefined;
        }
    }, [encodedUnsubscribeKeys]);

    // Query to get current email preferences
    const [{ data: emailPreferencesResult, fetching }] =
        useGetEmailPreferencesQuery({
            variables: {
                key1: decodedKeys?.key1 || "",
                key2: decodedKeys?.key2 || ""
            },
            pause: !decodedKeys?.key1 || !decodedKeys?.key2
        });

    // Mutation to update email preferences
    const [, updateEmailPreference] = useUpdateEmailPreferenceMutation();

    // Initialize preferences when data is loaded
    useEffect(() => {
        if (emailPreferencesResult?.emailPreferences) {
            const prefs = emailPreferencesResult.emailPreferences;
            const preferencesMap: Record<
                string,
                { emailPreferenceId: string; shouldSend: boolean }
            > = {};

            prefs.emailPreferences.forEach((pref) => {
                if (pref?.emailPreferenceType && pref?.emailPreferenceId) {
                    preferencesMap[pref.emailPreferenceType] = {
                        emailPreferenceId: pref.emailPreferenceId,
                        shouldSend: pref.shouldSend
                    };
                }
            });

            setPreferences(preferencesMap);

            // Even though 'ready' isn't used to change the UI, we use it because it's not 'error' or 'loading'
            // so it'll bypass those checks and go to the page we want to interact with.
            setPageState("ready");
        }
    }, [emailPreferencesResult]);

    // Handle preference toggle
    const handlePreferenceToggle = async (
        emailPreferenceId: string,
        value: boolean
    ) => {
        const { data, error } = await updateEmailPreference({
            input: {
                emailPreferenceId,
                shouldSend: value
            }
        });

        if (
            !data?.emailPreferences?.updateEmailPreference?.emailPreferenceId || error
        ) {
            showErrorNotification("Failed to update preferences");
        }
    };

    // Group preferences by category
    const groupedPreferences = useMemo(() => {
        const groups: Record<string, Array<{ type: string; config: any }>> = {};

        Object.entries(EMAIL_PREFERENCE_TYPES).forEach(([type, config]) => {
            if (!groups[config.category]) {
                groups[config.category] = [];
            }
            groups[config.category].push({ type, config });
        });

        return groups;
    }, []);

    if (pageState === "error") {
        return (
            <Container size="md" py="xl">
                <Paper shadow="sm" p="xl" radius="md">
                    <Stack gap="lg" align="center">
                        <Alert
                            icon={<Error color="error" />}
                            title="Error Loading Preferences"
                            color="var(--mantine-color-red-5)"
                            variant="light"
                            style={{ width: "100%" }}
                        >
                            <Text variant="body" size="sm" mb="md">
                                We encountered an error while loading your email
                                preferences.
                            </Text>
                            <Text variant="body-muted">
                                Please check your link or contact support for
                                assistance.
                            </Text>
                        </Alert>

                        <Button
                            onClick={() =>
                            (window.location.href =
                                "mailto:support@therapprove.com")
                            }
                            leftSection={<Email />}
                        >
                            Contact Support
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    if (pageState === "loading" || fetching) {
        return (
            <Container size="md" py="xl">
                <Paper shadow="sm" p="xl" radius="md">
                    <Center>
                        <Stack gap="lg" align="center">
                            <Loader size="lg" />
                            <Title order={3}>Loading Email Preferences</Title>
                            <Text variant="body-muted" ta="center">
                                Please wait while we load your email
                                preferences...
                            </Text>
                        </Stack>
                    </Center>
                </Paper>
            </Container>
        );
    }

    return (
        <Container size="md" style={{ maxWidth: "50rem" }}>
            <Paper shadow="sm" p="xl" radius="md">
                <Stack gap="xl">
                    {/* Header */}
                    <Stack gap="md" align="center">
                        <Title order={2}>Manage Email Preferences</Title>
                        <Text variant="body-muted" ta="center">
                            Manage which types of emails you receive from
                            Therapprove
                        </Text>
                    </Stack>

                    {/* Preferences by Category */}
                    {Object.entries(groupedPreferences).map(
                        ([category, categoryPreferences]) => (
                            <Stack key={category} gap="md">
                                <Group justify="space-between" align="center">
                                    <Title order={4}>{category}</Title>
                                    <Badge
                                        variant="light"
                                        style={{
                                            color: "var(--mantine-color-white)",
                                            backgroundColor: PRIMARY_COLOR
                                        }}
                                    >
                                        {categoryPreferences.length} preference
                                        {categoryPreferences.length !== 1
                                            ? "s"
                                            : ""}
                                    </Badge>
                                </Group>

                                <Stack gap="md">
                                    {categoryPreferences.map(
                                        ({ type, config }) => {
                                            const IconComponent = config.icon;
                                            const emailPreferenceId =
                                                preferences[type]
                                                    .emailPreferenceId;
                                            const shouldSend =
                                                preferences[type].shouldSend ??
                                                false;

                                            return (
                                                <Paper
                                                    key={type}
                                                    p="md"
                                                    withBorder
                                                >
                                                    <Group
                                                        justify="space-between"
                                                        align="flex-start"
                                                    >
                                                        <Group
                                                            gap="md"
                                                            align="flex-start"
                                                            style={{ flex: 1 }}
                                                        >
                                                            <IconComponent
                                                                style={{
                                                                    color: PRIMARY_COLOR
                                                                }}
                                                            />
                                                            <Stack
                                                                gap="xs"
                                                                style={{
                                                                    flex: 1
                                                                }}
                                                            >
                                                                <Text
                                                                    variant="body"
                                                                    fw={500}
                                                                >
                                                                    {
                                                                        config.label
                                                                    }
                                                                </Text>
                                                                <Text
                                                                    variant="body-muted"
                                                                    style={{
                                                                        whiteSpace:
                                                                            "normal"
                                                                    }}
                                                                >
                                                                    {
                                                                        config.description
                                                                    }
                                                                </Text>
                                                            </Stack>
                                                        </Group>
                                                        <Switch
                                                            styles={{
                                                                track: {
                                                                    backgroundColor:
                                                                        shouldSend
                                                                            ? PRIMARY_COLOR
                                                                            : "color-mix(in srgb, var(--mantine-color-black) 10%, transparent)"
                                                                }
                                                            }}
                                                            checked={shouldSend}
                                                            onChange={async (
                                                                event
                                                            ) =>
                                                                await handlePreferenceToggle(
                                                                    emailPreferenceId,
                                                                    event
                                                                        .currentTarget
                                                                        .checked
                                                                )
                                                            }
                                                            size="md"
                                                        />
                                                    </Group>
                                                </Paper>
                                            );
                                        }
                                    )}
                                </Stack>

                                {category !==
                                    Object.keys(groupedPreferences).slice(
                                        -1
                                    )[0] && <Divider />}
                            </Stack>
                        )
                    )}

                    <Button
                        onClick={() =>
                            (window.location.href = "https://therapprove.com")
                        }
                        leftSection={<Home />}
                    >
                        Visit Therapprove
                    </Button>
                </Stack>
            </Paper>
        </Container>
    );
};
